import { api } from "@/lib/api";
import {
  CreateTourAgency,
  createTourAgencyToDto,
  TourAgency,
  TourAgencyDomain,
  TourAgencyDtoSchema,
  tourAgencyFromDto,
} from "@/models";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "../use-toast";
import {
  getAllTourAgenciesQueryOptions,
  getTourAgenciesQueryOptions,
} from "./get.hooks";

const toastDomainMessageMap: Record<TourAgencyDomain, string> = {
  agency: "toast.tourAgencyTourCreated",
  tour: "toast.tourAgencyAgencyCreated",
};

const toastDomainErrorMessageMap: Record<TourAgencyDomain, string> = {
  agency: "toast.createTourAgencyTourError",
  tour: "toast.createTourAgencyAgencyError",
};

export function useCreateTourAgency(
  domain: TourAgencyDomain,
  domainId: string,
  opts?: {
    onSuccess?: (tourAgency: TourAgency) => void;
    onError?: () => void;
  },
) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: async (tourAgency: CreateTourAgency) => {
      const dto = createTourAgencyToDto(tourAgency);
      const result = await api.post(
        "/api/tour-agencies",
        dto,
        TourAgencyDtoSchema,
      );
      return tourAgencyFromDto(result);
    },
    onSuccess: (tourAgency) => {
      queryClient.setQueryData(
        getTourAgenciesQueryOptions(domain, domainId).queryKey,
        (old) => [...(old ?? []), tourAgency],
      );
      queryClient.setQueryData(
        getAllTourAgenciesQueryOptions.queryKey,
        (old) => [...(old ?? []), tourAgency],
      );
      toast({
        title: toastDomainMessageMap[domain],
        variant: "success",
      });
      opts?.onSuccess?.(tourAgency);
    },
    onError: () => {
      toast({
        title: toastDomainErrorMessageMap[domain],
        variant: "destructive",
      });
      opts?.onError?.();
    },
  });
}
